import { HoverTooltip } from "@/components/common/HoverTooltip";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  formatBookingTagLabel,
  resolveTagTooltip,
} from "@/lib/bookingTagTooltip";
import {
  BOOKING_STATE_LABELS,
  type BookingFlags,
  type BookingState,
} from "@/types/booking";
import type { RebookInfo } from "@/types/rebook";

export const BOOKING_STATE_BADGE_VARIANT: Record<
  BookingState,
  "default" | "secondary" | "destructive" | "outline" | "urgent" | "info"
> = {
  pending: "secondary",
  pending_book: "secondary",
  book: "default",
  book_restricted: "urgent",
  rolled: "urgent",
  stuffed: "info",
  shipped: "info",
  delivered: "default",
  cancelled: "destructive",
  rebooked: "outline",
};

interface BookingStateBadgeProps {
  state: BookingState;
  className?: string;
}

/** Keeps lifecycle badges on one line even when the table has wrap-lines on. */
export const BOOKING_STATE_BADGE_CLASS =
  "shrink-0 whitespace-nowrap leading-none [word-break:normal]";

export function BookingStateBadge({ state, className }: BookingStateBadgeProps) {
  return (
    <Badge
      variant={BOOKING_STATE_BADGE_VARIANT[state]}
      data-table-inline
      className={cn(BOOKING_STATE_BADGE_CLASS, className)}
    >
      {BOOKING_STATE_LABELS[state]}
    </Badge>
  );
}

/** Green when the rebook saved money, red when it cost more, violet otherwise. */
function rebookTagVariant(rebookInfo?: RebookInfo | null): BadgeProps["variant"] {
  const delta = rebookInfo?.pricing_delta;
  if (delta == null || delta === 0) return "info";
  return delta > 0 ? "success" : "destructive";
}

function tagVariant(
  tag: string,
  rebookInfo?: RebookInfo | null,
): BadgeProps["variant"] {
  if (tag === "REBOOKED") return rebookTagVariant(rebookInfo);
  if (tag === "Opportunity") return "default";
  if (tag === "Rolled") return "urgent";
  if (tag === "DG") return "destructive";
  if (tag === "DOCUMENTS_ARRIVED") return "success";
  if (tag === "DOCUMENTS_MISSING") return "urgent";
  return "info";
}

interface BookingTagsProps {
  tags: string[];
  flags?: BookingFlags;
  rebookInfo?: RebookInfo | null;
  /** `default` matches `BookingStateBadge`; `compact` suits dense tables. */
  size?: "default" | "compact";
}

export function BookingTags({
  tags,
  flags,
  rebookInfo,
  size = "compact",
}: BookingTagsProps) {
  if (tags.length === 0) {
    return null;
  }

  const badgeClass =
    size === "compact"
      ? "shrink-0 whitespace-nowrap px-1.5 py-0 text-[10px] leading-none"
      : "shrink-0 whitespace-nowrap";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => {
        const tooltip = resolveTagTooltip(tag, flags);
        const label = formatBookingTagLabel(tag);
        return (
          <HoverTooltip key={tag} content={tooltip}>
            <Badge
              variant={tagVariant(tag, rebookInfo)}
              className={tooltip ? cn(badgeClass, "cursor-help") : badgeClass}
              aria-label={tooltip ? `${label}: ${tooltip}` : label}
            >
              {label}
            </Badge>
          </HoverTooltip>
        );
      })}
    </div>
  );
}
